import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import {
  Upload, FileText, Loader2, AlertTriangle, CheckCircle, Shield,
  ChevronDown, ChevronUp, Trash2, ArrowRight, FileSearch, RefreshCw, Brain,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { ContextualReviewPanel } from "../components/contract-editor/ContextualReviewPanel";

const statusConfig = {
  uploaded: { label: "Ready to Analyze", color: "bg-slate-100 text-slate-700" },
  analyzing: { label: "Analyzing...", color: "bg-amber-100 text-amber-700" },
  analyzed: { label: "Analysis Complete", color: "bg-emerald-100 text-emerald-700" },
  imported: { label: "Imported", color: "bg-cyan-100 text-cyan-700" },
};

export default function SupplierReviewPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);

  const fetchReviews = useCallback(async () => {
    try {
      const res = await apiClient.get("/supplier-review");
      setReviews(res.data);
    } catch (e) { /* Supplier reviews load silently */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  // Poll for analyzing items
  useEffect(() => {
    const analyzing = reviews.some((r) => r.status === "analyzing");
    if (!analyzing) return;
    const interval = setInterval(fetchReviews, 4000);
    return () => clearInterval(interval);
  }, [reviews, fetchReviews]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="supplier-review-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Supplier Contract Review
          </h1>
          <p className="text-slate-500 text-sm mt-1">Upload and analyze supplier contracts with AI</p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="gap-2 rounded-sm" data-testid="upload-contract-btn">
          <Upload size={16} /> Upload Contract
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="animate-spin text-slate-400" size={32} />
        </div>
      ) : reviews.length === 0 ? (
        <Card>
          <CardContent className="py-20 text-center">
            <FileSearch size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500 mb-2">No supplier contracts uploaded yet</p>
            <p className="text-sm text-slate-400 mb-4">Upload a PDF or DOCX contract to get started with AI-powered review</p>
            <Button onClick={() => setUploadOpen(true)} variant="outline" className="gap-2 rounded-sm">
              <Upload size={16} /> Upload Your First Contract
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onSelect={() => setSelectedReview(review)}
              onRefresh={fetchReviews}
              navigate={navigate}
            />
          ))}
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onSuccess={fetchReviews} />
      {selectedReview && (
        <AnalysisDialog
          review={selectedReview}
          onClose={() => setSelectedReview(null)}
          onRefresh={fetchReviews}
          navigate={navigate}
        />
      )}
    </div>
  );
}

function ReviewCard({ review, onSelect, onRefresh, navigate }) {
  const sc = statusConfig[review.status] || statusConfig.uploaded;

  const handleAnalyze = async (e) => {
    e.stopPropagation();
    try {
      await apiClient.post(`/supplier-review/${review.id}/analyze`);
      toast.success("Analysis started. This may take a minute...");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start analysis");
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this supplier review?")) return;
    try {
      await apiClient.delete(`/supplier-review/${review.id}`);
      toast.success("Review deleted");
      onRefresh();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <Card
      className="cursor-pointer hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
      onClick={review.status === "analyzed" || review.status === "imported" ? onSelect : undefined}
      data-testid={`review-card-${review.id}`}
    >
      <CardContent className="py-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
            <FileText size={20} className="text-slate-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-medium truncate">{review.filename}</span>
              <Badge className={`rounded-sm text-xs ${sc.color}`}>{sc.label}</Badge>
              {review.status === "analyzing" && <Loader2 size={14} className="animate-spin text-amber-600" />}
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              {review.supplier_name && <span>Supplier: {review.supplier_name}</span>}
              {review.contract_type && <span>Type: {review.contract_type}</span>}
              {review.jurisdiction && <span>Jurisdiction: {review.jurisdiction}</span>}
              <span>{new Date(review.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
              {review.risk_review?.overall_risk_score && (
                <RiskBadge score={review.risk_review.overall_risk_score} />
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {review.status === "uploaded" && (
              <Button size="sm" onClick={handleAnalyze} className="gap-1.5 rounded-sm h-8 text-xs" data-testid={`analyze-btn-${review.id}`}>
                <RefreshCw size={14} /> Analyze with AI
              </Button>
            )}
            {(review.status === "analyzed" || review.status === "imported") && (
              <Button size="sm" variant="outline" onClick={onSelect} className="gap-1.5 rounded-sm h-8 text-xs" data-testid={`view-analysis-btn-${review.id}`}>
                <FileSearch size={14} /> View Analysis
              </Button>
            )}
            {review.status === "imported" && (
              <Button
                size="sm" variant="outline"
                className="gap-1.5 rounded-sm h-8 text-xs text-cyan-600 border-cyan-200"
                onClick={(e) => { e.stopPropagation(); navigate(`/contracts/${review.imported_contract_id}`); }}
                data-testid={`go-to-contract-${review.id}`}
              >
                <ArrowRight size={14} /> View in Register
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={handleDelete} className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" data-testid={`delete-review-${review.id}`}>
              <Trash2 size={14} />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RiskBadge({ score }) {
  const color = score >= 7 ? "bg-red-100 text-red-700" : score >= 4 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${color}`} data-testid="risk-score-badge">
      <AlertTriangle size={10} /> Risk: {score}/10
    </span>
  );
}

function UploadDialog({ open, onOpenChange, onSuccess }) {
  const [file, setFile] = useState(null);
  const [supplierName, setSupplierName] = useState("");
  const [contractType, setContractType] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (supplierName) formData.append("supplier_name", supplierName);
      if (contractType) formData.append("contract_type", contractType);
      if (jurisdiction) formData.append("jurisdiction", jurisdiction);

      await apiClient.post("/supplier-review", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Contract uploaded. Text extracted successfully.");
      onOpenChange(false);
      setFile(null);
      setSupplierName("");
      setContractType("");
      setJurisdiction("");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Supplier Contract</DialogTitle>
          <DialogDescription>Upload a PDF or DOCX file. The text will be extracted for AI analysis.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Contract File *</Label>
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-sm p-6 text-center hover:border-slate-300 transition-colors">
              <input
                type="file"
                accept=".pdf,.docx,.txt"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="hidden"
                id="contract-file-input"
                data-testid="file-input"
              />
              <label htmlFor="contract-file-input" className="cursor-pointer">
                <Upload size={24} className="mx-auto mb-2 text-slate-400" />
                {file ? (
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{file.name}</p>
                ) : (
                  <p className="text-sm text-slate-500">Click to select PDF, DOCX, or TXT file</p>
                )}
                <p className="text-xs text-slate-400 mt-1">Maximum 20MB</p>
              </label>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Supplier Name</Label>
            <Input
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              placeholder="e.g., Acme Corp"
              className="rounded-sm"
              data-testid="supplier-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Contract Type</Label>
            <Input
              value={contractType}
              onChange={(e) => setContractType(e.target.value)}
              placeholder="e.g., Service Agreement, NDA"
              className="rounded-sm"
              data-testid="contract-type-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Jurisdiction *</Label>
            <Input
              value={jurisdiction}
              onChange={(e) => setJurisdiction(e.target.value)}
              placeholder="e.g., England & Wales, New York, Singapore"
              className="rounded-sm"
              data-testid="jurisdiction-input"
            />
            <p className="text-[10px] text-slate-400">The jurisdiction of your organisation. AI risk analysis will evaluate compliance against laws applicable in this jurisdiction.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleUpload} disabled={uploading || !file} className="gap-2 rounded-sm" data-testid="confirm-upload-btn">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            Upload & Extract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnalysisDialog({ review, onClose, onRefresh, navigate }) {
  const [importing, setImporting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ risk: true, summary: true, obligations: false, milestones: false, text: false });
  const [contextualOpen, setContextualOpen] = useState(false);

  const toggleSection = (key) => setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await apiClient.post(`/supplier-review/${review.id}/import`);
      toast.success(`Imported as ${res.data.contract_unique_id}`);
      onRefresh();
      onClose();
      navigate(`/contracts/${res.data.contract_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  // Build formData shape for ContextualReviewPanel from the supplier review data
  const contextualFormData = {
    contract_type: review.contract_type || "",
    jurisdiction: "",
    contract_value: "",
    contracting_party: { legal_name: review.supplier_name || "" },
    sections: [],
    content: review.extracted_text || "",
  };

  const risk = review.risk_review;
  const summary = review.executive_summary;

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 gap-0" aria-describedby="analysis-desc">
        <DialogHeader className="px-6 pt-5 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>{review.filename}</DialogTitle>
              <p className="text-xs text-slate-500 mt-0.5" id="analysis-desc">
                {review.supplier_name && `${review.supplier_name} · `}
                {review.contract_type && `${review.contract_type} · `}
                AI Analysis
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={contextualOpen ? "default" : "outline"}
                className={`gap-1.5 rounded-sm h-8 text-xs ${contextualOpen ? "bg-violet-600 hover:bg-violet-700 text-white" : "border-violet-300 text-violet-700 hover:bg-violet-50"}`}
                onClick={() => setContextualOpen(!contextualOpen)}
                data-testid="buyer-intelligence-btn"
              >
                <Brain size={14} />
                {contextualOpen ? "Close" : "Buyer Intelligence"}
              </Button>
              {risk?.overall_risk_score && <RiskBadge score={risk.overall_risk_score} />}
              {summary?.approval_recommendation && (
                <Badge className={`rounded-sm text-xs font-bold ${
                  summary.approval_recommendation === "approve_with_confidence" ? "bg-emerald-100 text-emerald-700" :
                  summary.approval_recommendation === "approve_with_caution" ? "bg-amber-100 text-amber-700" :
                  "bg-red-100 text-red-700"
                }`} data-testid="recommendation-badge">
                  {summary.approval_recommendation.replace(/_/g, " ").toUpperCase()}
                </Badge>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="flex h-[60vh]">
          <ScrollArea className={contextualOpen ? "flex-1" : "w-full"}>
          <div className="px-6 py-4 space-y-4">
            {/* Executive Summary */}
            {summary && (
              <CollapsibleSection
                title="Executive Summary"
                icon={<FileText size={16} className="text-indigo-600" />}
                expanded={expandedSections.summary}
                onToggle={() => toggleSection("summary")}
                testId="section-summary"
              >
                <p className="text-sm text-slate-700 dark:text-slate-300 mb-3">{summary.overview}</p>
                {summary.recommendation_rationale && (
                  <p className="text-xs text-slate-500 italic mb-3">{summary.recommendation_rationale}</p>
                )}
                {summary.key_commercial_terms?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-mono uppercase text-slate-500 mb-1">Key Commercial Terms</p>
                    <ul className="space-y-0.5">
                      {summary.key_commercial_terms.map((t, i) => (
                        <li key={`term-${i}-${t?.slice(0,20)}`} className="text-sm text-slate-600 flex items-start gap-1.5">
                          <span className="text-slate-400">-</span> {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {summary.missing_protections?.length > 0 && (
                  <div className="p-3 border border-amber-200 bg-amber-50/50 rounded-sm">
                    <p className="text-xs font-mono uppercase text-amber-600 mb-1">Missing Protections</p>
                    <ul className="space-y-0.5">
                      {summary.missing_protections.map((p, i) => (
                        <li key={`prot-${i}-${p?.slice(0,20)}`} className="text-sm text-amber-800">- {p}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CollapsibleSection>
            )}

            {/* Risk Review */}
            {risk && (
              <CollapsibleSection
                title={`Risk Analysis (Score: ${risk.overall_risk_score}/10)`}
                icon={<AlertTriangle size={16} className="text-red-500" />}
                expanded={expandedSections.risk}
                onToggle={() => toggleSection("risk")}
                testId="section-risk"
              >
                <p className="text-sm text-slate-600 mb-3">{risk.summary}</p>
                {risk.strengths?.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-mono uppercase text-emerald-600 mb-1">Strengths</p>
                    <ul className="space-y-0.5">
                      {risk.strengths.map((s, i) => (
                        <li key={`str-${i}-${s?.slice(0,20)}`} className="text-sm text-slate-600 flex items-start gap-1.5">
                          <CheckCircle size={12} className="text-emerald-500 mt-0.5 shrink-0" /> {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {risk.risks?.length > 0 && (
                  <div className="space-y-2">
                    {risk.risks.map((r, i) => (
                      <div key={r.title || `risk-${i}`} className={`p-3 rounded-sm border ${
                        r.severity === "high" ? "border-red-200 bg-red-50/50" :
                        r.severity === "medium" ? "border-amber-200 bg-amber-50/50" :
                        "border-slate-200 bg-slate-50/50"
                      }`} data-testid={`risk-item-${i}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`rounded-sm text-[10px] ${
                            r.severity === "high" ? "bg-red-100 text-red-700" :
                            r.severity === "medium" ? "bg-amber-100 text-amber-700" :
                            "bg-slate-100 text-slate-600"
                          }`}>{r.severity}</Badge>
                          <span className="text-xs text-slate-400 uppercase">{r.category}</span>
                        </div>
                        <p className="text-sm font-medium text-slate-700">{r.description}</p>
                        {r.recommendation && <p className="text-xs text-slate-500 mt-1">Recommendation: {r.recommendation}</p>}
                        {r.suggested_clause && (
                          <div className="mt-2 p-2 bg-white dark:bg-slate-900 border rounded-sm">
                            <p className="text-[10px] font-mono uppercase text-slate-400 mb-0.5">Suggested Clause</p>
                            <p className="text-xs text-slate-600 italic">{r.suggested_clause}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {risk.missing_standard_clauses?.length > 0 && (
                  <div className="mt-3 p-3 border border-orange-200 bg-orange-50/50 rounded-sm">
                    <p className="text-xs font-mono uppercase text-orange-600 mb-1">Missing Standard Clauses</p>
                    <ul className="space-y-0.5">
                      {risk.missing_standard_clauses.map((c, i) => (
                        <li key={`clause-${i}-${c?.slice(0,20)}`} className="text-sm text-orange-800">- {c}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CollapsibleSection>
            )}

            {/* Obligations */}
            {review.obligations?.length > 0 && (
              <CollapsibleSection
                title={`Obligations (${review.obligations.length})`}
                icon={<Shield size={16} className="text-purple-600" />}
                expanded={expandedSections.obligations}
                onToggle={() => toggleSection("obligations")}
                testId="section-obligations"
              >
                <div className="space-y-2">
                  {review.obligations.map((o, i) => (
                    <div key={o.title || `ob-${i}`} className="p-2 border rounded-sm" data-testid={`obligation-${i}`}>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium">{o.title}</span>
                        <Badge className={`rounded-sm text-[10px] ${
                          o.priority === "high" ? "bg-red-100 text-red-700" :
                          o.priority === "medium" ? "bg-amber-100 text-amber-700" :
                          "bg-slate-100 text-slate-600"
                        }`}>{o.priority}</Badge>
                        <span className="text-[10px] text-slate-400 uppercase">{o.category}</span>
                      </div>
                      <p className="text-xs text-slate-500">{o.description}</p>
                      {o.responsible_party && <p className="text-[10px] text-slate-400 mt-0.5">Responsible: {o.responsible_party}</p>}
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Milestones */}
            {review.milestones?.length > 0 && (
              <CollapsibleSection
                title={`Milestones (${review.milestones.length})`}
                icon={<CheckCircle size={16} className="text-cyan-600" />}
                expanded={expandedSections.milestones}
                onToggle={() => toggleSection("milestones")}
                testId="section-milestones"
              >
                <div className="space-y-2">
                  {review.milestones.map((m, i) => (
                    <div key={m.title || `ms-${i}`} className="p-2 border rounded-sm" data-testid={`milestone-${i}`}>
                      <span className="text-sm font-medium">{m.title}</span>
                      <span className="text-[10px] text-slate-400 uppercase ml-2">{m.category}</span>
                      <p className="text-xs text-slate-500">{m.description}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Extracted Text */}
            <CollapsibleSection
              title="Extracted Text"
              icon={<FileText size={16} className="text-slate-400" />}
              expanded={expandedSections.text}
              onToggle={() => toggleSection("text")}
              testId="section-text"
            >
              <pre className="text-xs text-slate-600 whitespace-pre-wrap font-mono bg-slate-50 dark:bg-slate-900 p-3 rounded-sm border max-h-[300px] overflow-auto">
                {review.extracted_text}
              </pre>
            </CollapsibleSection>
          </div>
        </ScrollArea>

        {/* AI Buyer Intelligence Panel */}
        {contextualOpen && (
          <div className="w-[380px] shrink-0 border-l h-full" data-testid="buyer-intelligence-panel">
            <ContextualReviewPanel
              formData={contextualFormData}
              reviewMode="third_party_review"
              onClose={() => setContextualOpen(false)}
            />
          </div>
        )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t flex items-center justify-between">
          <p className="text-[10px] text-slate-400 italic max-w-md">
            This AI analysis is for informational purposes only and does not constitute legal advice.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
            {!review.imported_contract_id ? (
              <Button onClick={handleImport} disabled={importing} className="gap-2 rounded-sm" data-testid="import-to-register-btn">
                {importing ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                Import to Contract Register
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={() => { onClose(); navigate(`/contracts/${review.imported_contract_id}`); }}
                className="gap-2 rounded-sm text-cyan-600 border-cyan-200"
                data-testid="go-to-imported-contract-btn"
              >
                <ArrowRight size={16} /> View in Register
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CollapsibleSection({ title, icon, expanded, onToggle, children, testId }) {
  return (
    <div className="border rounded-sm" data-testid={testId}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{title}</span>
        </div>
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {expanded && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}
